import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@root/src/environments/environment';

export interface WeatherTileLayerViewModel {
  id: string;
  name: string;
  geoServerName: string;
  createAt: string;
  layerType: string;
  indicatorName: string;
  dataSource: string;
  layerDate?: string;
   hasTifFile: boolean;
   isDateWithoutYear: boolean;
   annotation?: string | null;
   /** TIF file size in KB */
   fileSizeKb?: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class WeatherTileLayerService {
  private readonly baseUrl = `${environment.api}/tiles/TileLayer`;

  constructor(private http: HttpClient) {}

  /** Format date in local time as YYYY-MM-DD so backend receives the selected calendar day (avoids UTC shift). */
  private toLocalDateString(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  getWeatherTileLayers(
    filters?: {
      year?: number;
      type?: number;
      indicatorName?: string;
      dataSource?: string;
      createAtFrom?: Date;
      createAtTo?: Date;
      layerDateFrom?: Date;
      layerDateTo?: Date;
    },
    take: number = 10,
    offset: number = 0
  ): Observable<WeatherTileLayerViewModel[]> {
    let params = new HttpParams()
      .set('take', take.toString())
      .set('offset', offset.toString());

    if (filters) {
      if (filters.year) {
        params = params.set('year', filters.year.toString());
      }
      if (filters.type) {
        params = params.set('type', filters.type.toString());
      }
      if (filters.indicatorName) {
        params = params.set('indicatorName', filters.indicatorName);
      }
      if (filters.dataSource) {
        params = params.set('dataSource', filters.dataSource);
      }
      if (filters.createAtFrom) {
        const dateStr = this.toLocalDateString(filters.createAtFrom);
        params = params.set('createAtFrom', `${dateStr}T00:00:00.000Z`);
      }
      if (filters.createAtTo) {
        const dateStr = this.toLocalDateString(filters.createAtTo);
        params = params.set('createAtTo', `${dateStr}T23:59:59.999Z`);
      }
      if (filters.layerDateFrom) {
        params = params.set('layerDateFrom', this.toLocalDateString(filters.layerDateFrom));
      }
      if (filters.layerDateTo) {
        params = params.set('layerDateTo', this.toLocalDateString(filters.layerDateTo));
      }
    }

    return this.http.get<WeatherTileLayerViewModel[]>(`${this.baseUrl}/GetWeatherTileLayers`, { params });
  }

  /**
   * API for DynamicDataTable / ListTableFiltersWrapper (params with offset, take, filters array).
   * Use with getAllMethod='getByQueryPost', getTotalMethod='getTotalCountPost'.
   */
  getByQueryPost(params: { offset?: number; take?: number; filters?: Array<{ filterField?: string; filterValue?: unknown }> }): Observable<WeatherTileLayerViewModel[]> {
    const take = params.take ?? 10;
    const offset = params.offset ?? 0;
    const filters = this.filtersFromParams(params.filters);
    return this.getWeatherTileLayers(filters, take, offset);
  }

  getTotalCountPost(params: { filters?: Array<{ filterField?: string; filterValue?: unknown }> }): Observable<number> {
    const filters = this.filtersFromParams(params.filters);
    return this.getWeatherTileLayersTotalCount(filters);
  }

  private filtersFromParams(
    filtersArr?: Array<{ filterField?: string; filterValue?: unknown }>
  ): {
    year?: number;
    type?: number;
    indicatorName?: string;
    dataSource?: string;
    createAtFrom?: Date;
    createAtTo?: Date;
    layerDateFrom?: Date;
    layerDateTo?: Date;
  } | undefined {
    if (!filtersArr?.length) return undefined;
    const out: Record<string, unknown> = {};
    for (const f of filtersArr) {
      if (f.filterField == null) continue;
      let v = f.filterValue;
      if (f.filterField === 'createAtFrom' || f.filterField === 'createAtTo' || f.filterField === 'layerDateFrom' || f.filterField === 'layerDateTo') {
        if (typeof v === 'string') v = new Date(v);
      }
      out[f.filterField] = v;
    }
    return out as {
      year?: number;
      type?: number;
      indicatorName?: string;
      dataSource?: string;
      createAtFrom?: Date;
      createAtTo?: Date;
      layerDateFrom?: Date;
      layerDateTo?: Date;
    };
  }

  getWeatherTileLayersTotalCount(filters?: {
    year?: number;
    type?: number;
    indicatorName?: string;
    dataSource?: string;
    createAtFrom?: Date;
    createAtTo?: Date;
    layerDateFrom?: Date;
    layerDateTo?: Date;
  }): Observable<number> {
    let params = new HttpParams();

    if (filters) {
      if (filters.year) {
        params = params.set('year', filters.year.toString());
      }
      if (filters.type) {
        params = params.set('type', filters.type.toString());
      }
      if (filters.indicatorName) {
        params = params.set('indicatorName', filters.indicatorName);
      }
      if (filters.dataSource) {
        params = params.set('dataSource', filters.dataSource);
      }
      if (filters.createAtFrom) {
        const dateStr = this.toLocalDateString(filters.createAtFrom);
        params = params.set('createAtFrom', `${dateStr}T00:00:00.000Z`);
      }
      if (filters.createAtTo) {
        const dateStr = this.toLocalDateString(filters.createAtTo);
        params = params.set('createAtTo', `${dateStr}T23:59:59.999Z`);
      }
      if (filters.layerDateFrom) {
        params = params.set('layerDateFrom', this.toLocalDateString(filters.layerDateFrom));
      }
      if (filters.layerDateTo) {
        params = params.set('layerDateTo', this.toLocalDateString(filters.layerDateTo));
      }
    }

    return this.http.get<number>(`${this.baseUrl}/GetWeatherTileLayersTotalCount`, { params });
  }

  downloadWeatherTileLayerTif(id: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/DownloadWeatherTileLayerTif/${id}`, { responseType: 'blob' });
  }

  updateWeatherTileLayerAnnotation(id: string, annotation: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/UpdateWeatherTileLayerAnnotation/${id}`, { annotation });
  }
}
